import { Avatar, SxProps, Theme } from "@mui/material";
import { AVATAR_PIXEL_SIZE_UNIT, AVATAR_SIZE_MAP, IAvatarSize } from "src/components-mui/base/avatar/avatar.constants";
import { buildAvatarImageSources, getAvatarPixelSize, toResponsiveValue } from "src/components-mui/base/avatar/avatar.utils";
import { IWfAvatarProps, WfAvatar } from "src/components-mui/base/avatar/wf-avatar";
import { toSxArray } from "src/utils/mui/sx/sx";
import { getImageBaseUrl } from "src/utils/responsive-image-util";

const FALLBACK_LOGO_ISIN = "000000000000";

interface IUnderlyingLogoByIsinProps extends Omit<IWfAvatarProps, "size" | "src" | "srcSet" | "children"> {
    isin: string;
    size: IAvatarSize | IAvatarSize[];
}

interface IUnderlyingLogoByUrlProps {
    logoUrl: string;
    size: IAvatarSize;
    alt?: string;
    sx?: SxProps<Theme>;
}

export type IUnderlyingLogoProps = IUnderlyingLogoByIsinProps | IUnderlyingLogoByUrlProps;

const getUnderlyingLogoUrl = ({ isin }: { isin: string }) => `${getImageBaseUrl()}images/underlying-logos/${isin.toUpperCase()}.png`;

const UnderlyingLogoByIsin = ({ isin, size, alt = "", loading = "lazy", sx, ...avatarProps }: IUnderlyingLogoByIsinProps) => {
    const dimension = toResponsiveValue(size, value => AVATAR_SIZE_MAP[value] * AVATAR_PIXEL_SIZE_UNIT);
    const logoSources = buildAvatarImageSources({ imageUrl: getUnderlyingLogoUrl({ isin }), size });
    const fallbackLogoSources = buildAvatarImageSources({ imageUrl: getUnderlyingLogoUrl({ isin: FALLBACK_LOGO_ISIN }), size });

    return (
        <WfAvatar
            sx={[
                { width: dimension, height: dimension, border: "1px solid", borderColor: "gray.100", backgroundColor: "common.white" },
                ...toSxArray(sx),
            ]}
            src={logoSources.src}
            srcSet={logoSources.srcSet}
            loading={loading}
            alt={alt}
            {...avatarProps}
        >
            <Avatar
                variant="square"
                src={fallbackLogoSources.src}
                srcSet={fallbackLogoSources.srcSet}
                alt={alt}
                slotProps={{ img: { loading } }}
                sx={{ width: "100%", height: "100%" }}
            />
        </WfAvatar>
    );
};

const UnderlyingLogoByUrl = ({ logoUrl, size, alt = "", sx }: IUnderlyingLogoByUrlProps) => {
    const pixelSize = getAvatarPixelSize(size);
    const logoSources = buildAvatarImageSources({ imageUrl: logoUrl, size });

    return (
        <Avatar
            src={logoSources.src}
            srcSet={logoSources.srcSet}
            alt={alt}
            sx={[
                {
                    width: pixelSize,
                    height: pixelSize,
                    outline: "1px solid",
                    outlineColor: "gray.100",
                    outlineOffset: "-1px",
                    backgroundColor: "common.white",
                },
                ...toSxArray(sx),
            ]}
        />
    );
};

export const UnderlyingLogo = (props: IUnderlyingLogoProps) =>
    "logoUrl" in props ? <UnderlyingLogoByUrl {...props} /> : <UnderlyingLogoByIsin {...props} />;
